/**
 * HealthWright Technologies — Sales Representative Partnership Cover Letter
 * Public URL: /cover-letter/healthwright-technologies
 *
 * Application is from Sterile Mate LLC as the sales rep organization (not
 * George Broughton personally). Mirrors the CV's design system + WeasyPrint
 * PDF pattern; PDF endpoint lives at /api/cv/healthwright/pdf.
 */
import { Link } from "react-router-dom";
import PublicSiteHeader from "@/components/PublicSiteHeader";
import Footer from "@/components/Footer";
import { Download, Printer, ArrowLeft } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PDF_URL = `${BACKEND_URL}/api/cv/healthwright/pdf`;
const PORTRAIT_URL = `${BACKEND_URL}/api/static/email_assets/george_broughton_portrait.jpg`;

const TODAY = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

export default function HealthWrightCoverLetterPage() {
  return (
    <div className="min-h-screen bg-white">
      <div className="print:hidden"><PublicSiteHeader /></div>

      <div className="print:hidden bg-slate-50 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/about" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="hw-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to About
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-white border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="hw-print-btn">
              <Printer className="w-4 h-4" /> Print
            </button>
            <a href={PDF_URL} className="inline-flex items-center gap-1.5 px-4 h-9 rounded-md bg-[#0056A8] hover:bg-[#003366] text-white text-sm font-semibold" data-testid="hw-download-pdf-btn">
              <Download className="w-4 h-4" /> Download PDF
            </a>
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-8 py-12 font-serif text-slate-800 leading-relaxed print:py-6 print:px-0" style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}>

        {/* Letterhead */}
        <header className="pb-6 mb-8 border-b-2 border-slate-900">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-cyan-700" style={{ fontFamily: "'Segoe UI', Arial, sans-serif" }}>Sterile Mate LLC</p>
              <p className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: "'Georgia', serif" }}>Sales Representative Partnership — Application</p>
            </div>
            <div className="text-right text-[12px] text-slate-600" style={{ fontFamily: "'Segoe UI', Arial, sans-serif" }}>
              <p className="font-semibold text-slate-900">Sterile Mate LLC</p>
              <p>Hardy · North Houston District</p>
              <p>Houston, TX</p>
              <p>george.broughton@sterilemate.com</p>
              <p>323.243.9139</p>
              <p>SAM.gov · CAGE 9GH66</p>
            </div>
          </div>
        </header>

        <p className="text-sm text-slate-600 mb-6">{TODAY}</p>

        <address className="not-italic mb-8 text-[14px]">
          <p className="font-semibold text-slate-900">HealthWright Technologies</p>
          <p>Sales Recruitment Team</p>
          <p>contact@healthwrighttechnologies.com</p>
        </address>

        <p className="font-bold mb-6 text-slate-900">
          Re: Sterile Mate LLC — Application for Sales Representative Partnership
        </p>

        <p className="mb-5">Dear HealthWright Technologies Team,</p>

        <p className="mb-5">
          I am writing on behalf of <strong>Sterile Mate LLC</strong>, a Houston-based, MBE-certified medical device distributor and healthcare facilities infrastructure partner, to formally express our interest in serving as an <strong>authorized sales representative organization</strong> for the HealthWright Technologies portfolio across our existing Texas Medical Center and Southern Gulf States footprint.
        </p>

        <p className="mb-5">
          Sterile Mate was founded in September 2020 to address the critical medical device backorder pressure facing Houston-area physician practices and hospital systems. Five years later, the firm operates as a multi-line distributor serving independent physicians, surgical groups, and hospital systems across the Texas Medical Center, the North Houston corridor, The Woodlands, and the broader Greater Houston metropolitan area — with active regional accounts extending into the Southern Gulf States. <strong>I personally serve as Sterile Mate&rsquo;s founding sales representative and principal point of contact today, and we are actively building out our sales team in 2026</strong> — which is precisely why the timing of a HealthWright partnership is strong: your portfolio would anchor the brand identity of the additional representatives we bring on. This is the exact independent-practice profile HealthWright Technologies is built to serve: physicians who need diagnostic and therapeutic capabilities that fill real gaps in private-practice infrastructure, delivered by a representative who understands clinical workflow, reimbursement, and the consultative selling cycle.
        </p>

        <p className="mb-3 font-bold text-slate-900">The operating rhythm HealthWright describes is already mine.</p>
        <p className="mb-5">
          We have read your overview of the day-in-the-life of a HealthWright sales representative. The pattern you describe — morning product demonstrations of the <strong>TM-Flow™ autonomic nervous system analyzer</strong> and the <strong>CMAT Advantage™ vascular diagnostic system</strong>, afternoon follow-ups optimizing workflow with existing practices, complementary positioning of the <strong>Hako-Med Horizontal Therapy</strong> system for pain-management expansion, and ongoing territory development through medical conferences and referral networks — is, in fact, the rhythm I execute for our medical device clients today. I walk into physician offices with diagnostic devices, integrate them into existing clinical workflows, support CPT-code-anchored reimbursement conversations, and provide post-install training to clinical staff. As Sterile Mate&rsquo;s sales team grows, that operating pattern is the model the additional representatives we onboard will follow under the brand standard a HealthWright partnership would set.
        </p>

        <p className="mb-3 font-bold text-slate-900">What Sterile Mate brings to a HealthWright partnership.</p>
        <ul className="mb-5 space-y-2.5 pl-5 list-disc marker:text-cyan-600">
          <li>
            <strong>Established physician relationships</strong> across one of the densest concentrations of independent practices in the country — the Texas Medical Center and the surrounding Greater Houston region — with active referrals into the Southern Gulf States.
          </li>
          <li>
            <strong>A proprietary operating platform — AskIsaac.io —</strong> that gives our representatives end-to-end CRM, demonstration scheduling, post-sale customer relationship management, and structured audit trails across every account interaction.
          </li>
          <li>
            <strong>Demonstrated clinical-workflow competency</strong> across AAMI ST108-compliant water infrastructure, sterile processing capital, and Population Health remote patient monitoring devices &mdash; meaning the sales team Sterile Mate is fielding speaks the clinical-team vocabulary HealthWright&rsquo;s demos require from day one.
          </li>
          <li>
            <strong>SAM.gov-registered, MBE-certified business posture (CAGE 9GH66)</strong> that opens additional public-sector and grant-funded buyer channels for HealthWright&rsquo;s clinical portfolio.
          </li>
          <li>
            <strong>A team operating against three published commitments</strong> — Service to Healthcare, Jobs for Our Community, and Sustainable Growth Earned — that align directly with the long-term-relationship philosophy your sales careers FAQ describes.
          </li>
        </ul>

        <p className="mb-3 font-bold text-slate-900">An additional procurement channel: VA &amp; federal Sole Source via SDVOSB.</p>
        <p className="mb-5">
          Sterile Mate maintains active working relationships with <strong>Service-Disabled Veteran-Owned Small Business (SDVOSB)</strong> entities. Under federal acquisition regulation, qualifying procurements may be set aside for SDVOSB primes on a <strong>sole-source basis</strong> — meaning HealthWright&rsquo;s diagnostic and therapeutic devices could be offered to the <strong>Veterans Health Administration</strong> and other federal-agency buyers as a sole-source acquisition rather than a competitive bid. This is a meaningful revenue channel that very few non-SDVOSB sales organizations can credibly open for a manufacturer, and it positions HealthWright&rsquo;s portfolio for a federal-health buying cycle that compounds alongside the independent-practice work.
        </p>

        <p className="mb-3 font-bold text-slate-900">Training, ramp, and territory readiness.</p>
        <p className="mb-5">
          While we bring meaningful B2B medical-device sales experience to this conversation, we recognize and welcome HealthWright&rsquo;s comprehensive training program covering clinical applications, reimbursement strategy, and consultative selling techniques specific to independent practices. We are prepared to commit our representatives to that training, to maintain demonstration units in our Hardy / North Houston District facility, and to invest in territory development at the pace your growth plan requires. <strong>We can begin coverage of the Greater Houston territory immediately</strong>, with structured expansion into adjacent Southern Gulf States markets as our footprint matures.
        </p>

        <p className="mb-5">
          I would welcome the opportunity to discuss how a Sterile Mate – HealthWright Technologies sales partnership would accelerate your southeastern expansion plan. Sterile Mate is registered, credentialed, and ready to demonstrate the same diligence on your portfolio that we apply to every client relationship we hold.
        </p>

        <p className="mb-8">
          Please feel free to contact me directly at the number or email above, or reply with a calendar window that works for your team.
        </p>

        <p className="mb-1">With sincere thanks for your time,</p>

        {/* Signature block with portrait */}
        <div className="flex items-end gap-4 mt-8">
          <img
            src={PORTRAIT_URL}
            alt="George Broughton III"
            className="w-20 h-20 rounded-lg object-cover border border-slate-300 print:rounded shadow-sm"
          />
          <div className="pb-1" style={{ fontFamily: "'Segoe UI', Arial, sans-serif" }}>
            <p className="text-2xl text-slate-900" style={{ fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive" }}>George Broughton III</p>
            <p className="font-bold text-slate-900 mt-1">George Broughton III</p>
            <p className="text-sm text-slate-700">Founder &amp; Chief Executive Officer</p>
            <p className="text-sm text-slate-700">Sterile Mate LLC</p>
            <p className="text-sm text-slate-600">george.broughton@sterilemate.com · 323.243.9139</p>
            <p className="text-sm text-slate-600">sterilemate.com · sterilemate.com · SAM.gov CAGE 9GH66</p>
          </div>
        </div>
      </main>

      <div className="print:hidden"><Footer /></div>

      <style>{`
        @media print {
          @page { size: Letter; margin: 0.6in; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          a { color: #0056A8 !important; text-decoration: none !important; }
        }
      `}</style>
    </div>
  );
}
